import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Database, HardDrive, RefreshCw, ScanFace } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const fmtBytes = (n) => {
  if (n === null || n === undefined) return "–";
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)} KB`;
  return `${n} B`;
};

const STATE = {
  ok: { dot: "bg-emerald-500", text: "text-emerald-700", key: "healthOk" },
  loading: { dot: "bg-amber-500 animate-pulse", text: "text-amber-700", key: "healthLoading" },
  down: { dot: "bg-rose-500", text: "text-rose-700", key: "healthDown" },
};

const stateOf = (status) =>
  status === "ok" ? STATE.ok : status === "loading" ? STATE.loading : STATE.down;

const Tile = ({ icon: Icon, label, status, lines, testId }) => {
  const s = stateOf(status);
  const { t } = useLang();
  return (
    <div data-testid={testId}
      className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold tracking-tight">{label}</p>
        <p className={`mt-0.5 flex items-center gap-1.5 text-xs font-medium ${s.text}`}>
          <span className={`h-2 w-2 rounded-full ${s.dot}`} />
          {t(s.key)}
        </p>
        {lines.filter(Boolean).map((l, i) => (
          <p key={i} title={l} className={`mt-0.5 text-xs text-muted-foreground ${s === STATE.ok ? "truncate" : "break-words"}`}>{l}</p>
        ))}
      </div>
    </div>
  );
};

/* Admin-only at-a-glance status of the face engine, MongoDB and the media
   storage volume. Auto-refreshes every 30s; manual refresh button. */
export const SystemHealthCard = () => {
  const { t } = useLang();
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["system-health"],
    queryFn: async () => (await api.get("/admin/system-health")).data,
    refetchInterval: 30000,
  });

  return (
    <div data-testid="system-health-card" className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t("systemHealth")}
        </h2>
        <Button size="sm" variant="ghost" onClick={() => refetch()}
          data-testid="system-health-refresh" disabled={isFetching}>
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
          {t("refreshHealth")}
        </Button>
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-3">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
        </div>
      ) : data ? (
        <div className="grid gap-3 sm:grid-cols-3">
          <Tile
            icon={ScanFace}
            label={t("healthFaceEngine")}
            status={data.faceService.status}
            testId="health-face-tile"
            lines={[
              data.faceService.status === "ok"
                ? t("healthFaceReady")
                : data.faceService.detail || t("faceEngineLoading"),
              ...(data.faceService.pendingVerifications > 0
                ? [`${data.faceService.pendingVerifications} ${t("facePendingVerifications")} — ${t("facePendingHint")}`]
                : []),
            ]}
          />
          <Tile
            icon={Database}
            label={t("healthDatabase")}
            status={data.database.status === "ok" ? "ok" : "down"}
            testId="health-db-tile"
            lines={[
              data.database.status === "ok"
                ? `${data.database.latencyMs} ms · ${data.database.users} ${t("healthUsers")} · ${data.database.punches} ${t("healthPunches")}`
                : data.database.detail,
            ]}
          />
          <Tile
            icon={HardDrive}
            label={t("healthStorage")}
            status={data.storage.status === "ok" ? "ok" : "down"}
            testId="health-storage-tile"
            lines={
              data.storage.status === "ok"
                ? [
                    `${fmtBytes(data.storage.freeBytes)} ${t("healthFreeOf")} ${fmtBytes(data.storage.totalBytes)}`,
                    `${data.storage.mediaFiles} ${t("healthPhotos")} (${fmtBytes(data.storage.mediaBytes)}) · ${t("healthRetention")} ${data.storage.retentionDays}d`,
                  ]
                : [data.storage.detail]
            }
          />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t("healthUnavailable")}</p>
      )}
    </div>
  );
};
